import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'advanced-filters/store';
import { defaultState } from 'advanced-filters/store/filter';
import type { CriteriaItem } from 'advanced-filters/types/filter';
import { hasSubCondition } from 'advanced-filters/utils/hasSubCondition';

const filtersSelector = (state: RootState) => state.advancedFilters.filter;

const baseSelector = createSelector(
  filtersSelector,
  (filter) => filter.allEntities[filter.selectedEntity!] ?? defaultState,
);

export const baseComparatorSelector = createSelector(
  baseSelector,
  (filter) => filter?.comparator,
);

export const pendingFilterSelector = createSelector(
  baseSelector,
  (filter) => filter.pendingFilter,
);

export const pendingFilterErrorSelector = createSelector(
  baseSelector,
  (filter) => filter.pendingFilterError,
);

export const allFiltersSelector = createSelector(
  baseSelector,
  (filter) => filter.filters,
);

export const selectedBaseConditionDataType = createSelector(
  pendingFilterSelector,
  (filter) => filter.conditionKey?.dataType || 'default',
);

export const fetchErrorSelector = createSelector(
  baseSelector,
  (filter) => filter.fetchError,
);

export const subConditionVisibleSelector = createSelector(
  pendingFilterSelector,
  (filter) => hasSubCondition(filter.conditionKey, filter.conditionValue),
);

export const filterLevelsSelector = createSelector(
  baseSelector,
  (filter) => filter.levels ?? [],
);

export const selectedLevelSelector = createSelector(
  baseSelector,
  (filter) => filter.selectedLevel ?? 0,
);

export const selectedLevelFiltersSelector = createSelector(
  [filterLevelsSelector, selectedLevelSelector],
  (levels, selectedLevel) => {
    const bucket = levels.find(
      (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === selectedLevel,
    );
    return bucket?.criteria ?? [];
  },
);

export const selectedLevelOperatorSelector = createSelector(
  [filterLevelsSelector, selectedLevelSelector],
  (levels, selectedLevel) => {
    const bucket = levels.find(
      (item): item is CriteriaItem & { type: 'level' } => item.type === 'level' && item.level === selectedLevel,
    );
    return bucket?.operator ?? 'AND';
  },
);

export const appliedFilterSelector = (entityName: string) => createSelector(
  filtersSelector,
  (filter) => {
    const state = filter.allEntities[entityName] ?? defaultState;
    return state.appliedFilters;
  },
);

export const isAnyConditionAppliedSelector = (entityName: string) => createSelector(
  appliedFilterSelector(entityName),
  (appliedFilters) => appliedFilters.levels.some((level) => level.criteria.length),
);
